import React from 'react';
import { Box, Container, LinearProgress, Paper, Stack } from '@mui/material';

const defaultPalette = {
  trackColor: 'rgba(148,163,184,0.2)',
  barGradient: 'linear-gradient(90deg, #0D1C4F 0%, #1E3A8A 100%)',
  barShadow: '0 0 10px rgba(30,58,138,0.25)',
  shimmerAnimation: 'shimmer 2s ease-in-out infinite alternate',
  barHeight: 5,
  barRadius: 4,
  dotGradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
  dotShadow: '0 2px 8px rgba(30,58,138,0.4)',
  dotSize: 10,
};


const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const AuthStepper = React.forwardRef((props, ref) => {
  const {
    currentStep = 1,
    totalSteps,
    progressValue,
    palette = {},
    containerProps = {},
    paperProps = {},
    progressWrapperProps = {},
    progressProps = {},
    dotProps = {},
    showDot = true,
    leftSlot,
    rightSlot,
    content,
    contentStackProps = {},
  } = props;
  const mergedPalette = { ...defaultPalette, ...palette };
  const stepCount = totalSteps ?? 0;
  const maxStep = stepCount > 0 ? stepCount : Math.max(currentStep, 1);
  const safeCurrentStep = clamp(currentStep, 1, maxStep);

  let resolvedProgress = typeof progressValue === 'number' ? progressValue : undefined;
  if (resolvedProgress === undefined) {
    if (stepCount > 1) {
      resolvedProgress = ((safeCurrentStep - 1) / (stepCount - 1)) * 100;
    } else if (safeCurrentStep > 1) {
      resolvedProgress = 100;
    } else {
      resolvedProgress = 0;
    }
  }

  const { sx: containerSx, ref: containerRef, maxWidth = 'md', ...restContainer } = containerProps;
  const { sx: paperSx, className: paperClassName, elevation = 0, ...restPaper } = paperProps;
  const { sx: wrapperSx, className: wrapperClassName, ...restWrapper } = progressWrapperProps;
  const { sx: progressSx, className: progressClassName, ...restProgress } = progressProps;
  const { sx: dotSx, ...restDot } = dotProps;
  const { sx: stackSx, ...restStack } = contentStackProps;

  const assignContainerRef = (node) => {
    if (typeof containerRef === 'function') {
      containerRef(node);
    } else if (containerRef && typeof containerRef === 'object') {
      containerRef.current = node;
    }

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref && typeof ref === 'object') {
      ref.current = node;
    }
  };

  const progressNode = (
    <LinearProgress
      variant="determinate"
      value={clamp(resolvedProgress, 0, 100)}
      className={progressClassName}
      sx={{
        height: mergedPalette.barHeight,
        borderRadius: mergedPalette.barRadius,
        backgroundColor: mergedPalette.trackColor,
        '& .MuiLinearProgress-bar': {
          background: mergedPalette.barGradient,
          borderRadius: mergedPalette.barRadius,
          boxShadow: mergedPalette.barShadow,
          animation: mergedPalette.shimmerAnimation,
        },
        ...progressSx,
      }}
      {...restProgress}
    />
  );

  const dotNode = showDot ? (
    <Box
      sx={{
        position: 'absolute',
        top: -2,
        right: -1,
        width: mergedPalette.dotSize,
        height: mergedPalette.dotSize,
        borderRadius: '50%',
        background: mergedPalette.dotGradient,
        boxShadow: mergedPalette.dotShadow,
        ...dotSx,
      }}
      {...restDot}
    />
  ) : null;

  const defaultContent = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ gap: 1, ...stackSx }}
      {...restStack}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {leftSlot}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {rightSlot}
      </Box>
    </Stack>
  );

  return (
    <Container
      ref={assignContainerRef}
      maxWidth={maxWidth}
      sx={{ mb: 2, ...containerSx }}
      {...restContainer}
    >
      <Paper
        className={paperClassName}
        elevation={elevation}
        sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 4, ...paperSx }}
        {...restPaper}
      >
        <Box
          className={wrapperClassName}
          sx={{ position: 'relative', mb: 1, ...wrapperSx }}
          {...restWrapper}
        >
          {progressNode}
          {dotNode}
        </Box>
        {content ?? defaultContent}
      </Paper>
    </Container>
  );
});

export default AuthStepper;
